import { PortableText } from "@portabletext/react";
import imageUrlBuilder from "@sanity/image-url";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { Users, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { d } from "../client";
import type { MeetTheTeamSection as Data } from "../sanity-schema";

gsap.registerPlugin(ScrollTrigger);

const builder = imageUrlBuilder({
  projectId: "om0huuvj",
  dataset: "production",
});

type SanityImageData = {
  asset?: { _ref: string; _type: "reference" };
  hotspot?: { x?: number; y?: number };
  crop?: unknown;
  _type: "image";
};

function useSanityUrl(image: SanityImageData | undefined) {
  return useMemo(() => {
    const ref = image?.asset?._ref;
    if (!ref) return null;
    return builder.image(image).width(1200).url();
  }, [image]);
}

/* ------------------------------------------------------------------ */
/*  Bio Card                                                           */
/* ------------------------------------------------------------------ */
const BioCard: React.FC<{
  member: NonNullable<Data["teamMembers"]>[0];
}> = ({ member }) => {
  const { name, roles, bio, headShot, funnyImage } = member;
  const headShotUrl = useSanityUrl(headShot);
  const funnyUrl = useSanityUrl(funnyImage);
  const [isOpen, setIsOpen] = useState(false);
  const [showFunny, setShowFunny] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!headShotUrl) return null;

  const hotspot = headShot?.hotspot;
  const objectPosition = hotspot
    ? `${(hotspot.x ?? 0.5) * 100}% ${(hotspot.y ?? 0.5) * 100}%`
    : "center 25%";

  return (
    <>
      <div className="group opacity-0" data-card="">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: tap-to-toggle on mobile */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: tap-to-toggle on mobile */}
        <div
          className={`relative aspect-[3/4] rounded-2xl overflow-hidden shadow-sm border border-gray-100 ${funnyUrl ? "cursor-pointer" : ""}`}
          onClick={() => {
            if (funnyUrl) setShowFunny((prev) => !prev);
          }}
          onMouseEnter={() => funnyUrl && setShowFunny(true)}
          onMouseLeave={() => funnyUrl && setShowFunny(false)}
        >
          {/* Headshot */}
          {/* biome-ignore lint/performance/noImgElement: Sanity CMS images with dynamic URLs */}
          <img
            src={headShotUrl}
            alt={name ?? ""}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            style={{ objectPosition }}
          />

          {/* Funny image overlay — tap on mobile, hover on desktop */}
          {funnyUrl && (
            // biome-ignore lint/performance/noImgElement: Sanity CMS images with dynamic URLs
            <img
              src={funnyUrl}
              alt={`${name} fun`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-400 ease-out ${
                showFunny ? "opacity-100" : "opacity-0"
              }`}
              style={{ objectPosition }}
            />
          )}

          {/* Tap hint on mobile (only when funny image exists) */}
          {funnyUrl && !showFunny && (
            <div className="[@media(hover:hover)]:hidden absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-sm rounded-full px-2.5 py-1">
              <span className="text-[10px] text-white font-medium">Tap me</span>
            </div>
          )}

          {/* Bottom gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Name + role overlay */}
          <div className="absolute inset-x-0 bottom-0 p-5">
            <h3 className="text-lg font-semibold text-white">{name}</h3>
            {d(roles, []).map((title) => (
              <p key={title} className="text-xs text-white/80 uppercase tracking-wider mt-0.5">
                {title}
              </p>
            ))}
          </div>
        </div>

        {/* View Bio button */}
        {bio && bio.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setShowFunny(false);
              setIsOpen(true);
            }}
            className="mt-3 w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:border-brand-mint hover:bg-brand-mint/10 hover:text-brand-black transition-all cursor-pointer"
          >
            View Bio
          </button>
        )}
      </div>

      {/* Bio Modal */}
      {isOpen && (
        // biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop dismiss
        // biome-ignore lint/a11y/useKeyWithClickEvents: modal backdrop dismiss
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-overlay-in" />
          {/* biome-ignore lint/a11y/noStaticElementInteractions: modal content stop propagation */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: modal content stop propagation */}
          <div
            className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl overflow-hidden animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header with image */}
            <div className="relative h-48 sm:h-56">
              {/* biome-ignore lint/performance/noImgElement: Sanity CMS images with dynamic URLs */}
              <img
                src={headShotUrl}
                alt={name ?? ""}
                className="w-full h-full object-cover"
                style={{ objectPosition }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6">
                <h3 className="text-xl font-bold text-white">{name}</h3>
                {d(roles, []).map((title) => (
                  <p key={title} className="text-xs text-white/80 uppercase tracking-wider mt-0.5">
                    {title}
                  </p>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3 flex items-center justify-center size-8 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Bio content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-14rem)]">
              <div className="text-sm text-gray-700 leading-relaxed space-y-4">
                <PortableText
                  value={bio ?? []}
                  components={{
                    block: {
                      normal: ({ children }) => <p className="min-h-[1lh]">{children}</p>,
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
export const MeetTheTeamSection: React.FC<{
  data: Data;
}> = ({ data }) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      gsap.from(el.querySelector("[data-section-header]"), {
        y: 40,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
        },
      });

      gsap.fromTo(
        el.querySelectorAll("[data-card]"),
        { y: 50, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
          },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 lg:py-20">
      <div className="max-w-5xl mx-auto px-6">
        <h2
          data-section-header=""
          className="flex items-center justify-center gap-3 text-2xl lg:text-4xl font-bold text-brand-black tracking-tight text-center"
        >
          <Users className="size-7 lg:size-9 text-brand-mint" />
          Meet the Team
        </h2>
        <p className="text-base text-gray-500 mt-3 max-w-lg mx-auto text-center leading-relaxed">
          The people behind the meals, dedicated to helping you live your healthiest life.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {d(data.teamMembers, []).map((member) => (
            <BioCard key={member._key} member={member} />
          ))}
        </div>
      </div>
    </section>
  );
};
